import TextInput from "./TextInput.js";
import TextSuggestion from "./TextSuggestion.js";
import NextButton from "./NextButton.js";
import { validEmail, validPass, validLogin } from "../validation.js";
import withStateSubmit from "./withStateSubmit.js";
import {
  FirstStateSubmit,
  FirstState,
  StringMap,
  Target,
  KeyboardEvent,
} from "../types/types.js";

import { trns } from "../helper.js";
import { clsx } from "clsx";
import { Segment, Item, Heading, Button, Form } from "ui-components";
import Eye from "../styles/svg/Eye.js";
import useRegStore from "../store/index.js";
import { createRef, useState, MouseEvent } from "react";

type Props = {
  handleField: (field: string, v: Target) => void;
  state: FirstState;
  dirty: StringMap<boolean>;
  submitted: boolean;
  onSubmit: (e: KeyboardEvent | MouseEvent<HTMLButtonElement>) => Promise<void>;
  reqOrOpt: (field: string) => typeof field;
  isReq: (field: string) => typeof field;
  changeState: (field: string, v?: string | number | boolean) => void;
  isFinal: boolean;
  isStart: boolean;
  cancel?: (e: MouseEvent<HTMLButtonElement>) => void;

  // Refs
  email_ref: typeof createRef;
  login_ref: typeof createRef;
  password_ref: typeof createRef;
};

const state: FirstState = {
  email: "",
  login: "",
  password: "",
};

const First = ({
  state,
  handleField,
  onSubmit,
  reqOrOpt,
  isReq,
  dirty,
  isStart,
  isFinal,
  cancel,
  submitted,
  changeState,
  email_ref,
  login_ref,
  password_ref,
}: Props) => {
  const [passwordVisibility, setPasswordVisibility] = useState(false);
  const togglePasswordVisibility = () => {
    setPasswordVisibility(prevPasswordVisibility => !prevPasswordVisibility);
  };

  const suggestions = useRegStore(state => state.suggestions);

  return (
    <Form className="fieldset">
      <Heading headingLevel="h4">
        {trns("IDS_REGISTRATION_ACCOUNT_INFORMATION", "Account Information")}
      </Heading>

      <Segment className="form-groups">
        {reqOrOpt("email") && (
          <TextInput
            placeholder={trns("IDS_LOGINBOX_EMAIL_ADDRESS", "Email")}
            label={trns("IDS_LOGINBOX_EMAIL_ADDRESS_PROMPT", "Email")}
            value={state.email}
            validator={validEmail}
            dirty={dirty.email}
            type="email"
            req={isReq("email")}
            onInput={v => handleField("email", v)}
            ref={email_ref}
          />
        )}

        {reqOrOpt("login") && (
          <TextSuggestion
            placeholder={trns("IDS_LOGINBOX_USERNAME", "Username")}
            label={trns("IDS_LOGINBOX_USERNAME_PROMPT", "Username:")}
            value={state.login}
            validator={validLogin}
            dirty={dirty.login}
            req={isReq("login")}
            onInput={v =>
              handleField("login", {
                ...v,
                target: { ...v.target, value: String(v.target.value).replace(/[^A-Za-z0-9_-]/g, "") },
              })
            }
            ref={login_ref}
            suggestions={suggestions}
            populateLogin={(s: string) => changeState("login", s)}
            noAutoComplete
          />
        )}

        {reqOrOpt("password") && (
          <Segment className="form-group">
            <TextInput
              placeholder={trns("IDS_LOGINBOX_PASSWORD", "Password")}
              label={trns("IDS_LOGINBOX_PASSWORD_PROMPT", "Password:")}
              className="field-password"
              value={state.password}
              validator={validPass}
              dirty={dirty.password}
              type={passwordVisibility ? "text" : "password"}
              req={isReq("password")}
              onInput={v => handleField("password", v)}
              ref={password_ref}
              noAutoComplete
            />

            <Button
              type="button"
              className={clsx({ "toggle-password": true, active: passwordVisibility })}
              onClick={togglePasswordVisibility}
            >
              <Eye />
            </Button>
          </Segment>
        )}
      </Segment>

      <Segment className="btn-toolbar reg-nav x">
        {cancel && !isStart && (
          <Item className="btn-cancel grid grid-6">
            <Button className="btn" onClick={cancel} />
          </Item>
        )}

        {isStart && (
          <Item className="grid grid-6">
            <Button className="btn" disabled />
          </Item>
        )}

        <Item className="grid grid-6">
          <NextButton
            onSubmit={onSubmit}
            disabled={submitted}
            isStart={isStart}
            isFinal={isFinal}
          />
        </Item>
      </Segment>
    </Form>
  );
};

export default withStateSubmit(First, state, "step1", (t: FirstStateSubmit) => ({
  email_ref: (el: typeof createRef) => {
    t.email_ref = el;
  },
  login_ref: (el: typeof createRef) => {
    t.login_ref = el;
  },
  password_ref: (el: typeof createRef) => {
    t.password_ref = el;
  },
}));
